import { Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmEventType } from 'primeng/api';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { DateTableColumn } from 'src/app/core/models/date-table-column';
import { LaasOption } from 'src/app/core/models/laas-option';
import { FacilityOptions } from 'src/app/core/models/options/facility-options';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { AutoPrintSettingTableHeaderData } from '../../interface/auto-print-setting-header-data';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { AutoPrintSettingService } from 'src/app/features/setting/service/auto-print-setting.service';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { AutoPrintSettingGetHeaderRequest } from '../../interface/auto-print-setting-get-header-request';
import { finalize } from 'rxjs/operators';
import { deepCopy, isNullOrEmpty, laaSFormatString, nullIfEmptyArray } from 'src/app/core/utils/common-functions';
import { throwError } from 'rxjs';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { DataTableSavedBean } from 'src/app/shared/components/data-table/bean/data-table-saved-bean';
import { PageInfo } from 'src/app/core/models/page-info';
import { SortOrder } from 'src/app/core/interface/sort-order';
import { UserType } from 'src/app/core/enums/user-type.enum';

@Component({
  selector: 'laas-auto-print-setting-header',
  templateUrl: './auto-print-setting-header.component.html',
  styleUrls: ['./auto-print-setting-header.component.scss']
})
export class AutoPrintSettingHeaderComponent extends SessionBaseComponent implements OnInit {

  @ViewChild(DataTableComponent) table!: DataTableComponent;
  @ViewChild('headerTable') headerTable!: DataTableComponent;

  // AccordionTab Control
  isOpenedSettingSearchFormTab: boolean = true;
  isOpenedSettingHeaderTab: boolean = false;

  searchFormGroup: FormGroup;

  // Options
  @Input() facilityOptions: FacilityOptions[] = [];
  docTypeOptions: LaasOption[] = [];
  deliveryTypeOptions: LaasOption[] = [];
  isLoopGroupOptions: LaasOption[] = [];
  isDeleteOptions: LaasOption[] = [];

  editPermission: boolean = false;

  readonly headerPreferenceKey: string = UserPreferenceConstants.AutoPrintSettingTableHeaderData;

  isHeaderTableLoading: boolean = false;

  // DateTableColumn
  columnOfAutoPrintSetting: DateTableColumn[] = [
    { field: 'facility', header: 'AutoPrintSetting.HeaderTable.Column.Facility', sort: true },
    { field: 'deliveryType', header: 'AutoPrintSetting.HeaderTable.Column.DeliveryType', sort: true },
    { field: 'docType', header: 'AutoPrintSetting.HeaderTable.Column.DocType', sort: true },
    { field: 'numberOfCopies', header: 'AutoPrintSetting.HeaderTable.Column.NumberOfCopies', sort: true },
    { field: 'printOrder', header: 'AutoPrintSetting.HeaderTable.Column.PrintOrder', sort: true },
    { field: 'isLoopGroup', header: 'AutoPrintSetting.HeaderTable.Column.IsLoopGroup', sort: true },
    { field: 'createUser', header: 'AutoPrintSetting.HeaderTable.Column.CreateUser', sort: true },
    { field: 'createDt', header: 'AutoPrintSetting.HeaderTable.Column.CreateDt', sort: true },
    { field: 'updateUser', header: 'AutoPrintSetting.HeaderTable.Column.UpdateUser', sort: true },
    { field: 'updateDt', header: 'AutoPrintSetting.HeaderTable.Column.UpdateDt', sort: true },
    { field: 'isDelete', header: 'AutoPrintSetting.HeaderTable.Column.IsDelete', sort: true },
    { field: 'consigneeKey', header: 'AutoPrintSetting.HeaderTable.Column.ConsigneeKey', sort: true },
    { field: '__action', header: '動作', sort: false },
  ];

  settingHeaderData: AutoPrintSettingTableHeaderData[] = [];

  permissionCode: PermissionConstants = PermissionConstants.ViewAutoPrintSetting;
  readonly editPermissionCode: PermissionConstants = PermissionConstants.EditAutoPrintSetting;

  // Table paging info
  pageInfo: PageInfo = {
    pageSize: 5,
    pageIndex: 0,
    totalRecords: 0,
    totalPages: 0,
  };

  sortOrder: SortOrder = {
    property: "SeqNo",
    direction: -1
  };

  constructor(
    private injector: Injector,
    private formBuilder: FormBuilder,
    private dropdownOptionsService: DropdownOptionsService,
    private autoPrintSettingService: AutoPrintSettingService,

  ) {
    super(injector);
    this.searchFormGroup = this.formBuilder.group({
      facility: [this.laasSetting.facility, Validators.required],
      deliveryTypes: [null, Validators.required],
      docTypes: [null, Validators.required]
    });

    this.deliveryTypeOptions = [];

    this.isLoopGroupOptions = [];

    this.isDeleteOptions = [];

  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.deliveryTypeOptions = [
      { label: 'Local', value: 'Local' },
      { label: 'Export', value: 'Export' },
    ];

    this.isLoopGroupOptions = [
      { label: '0 - 一般列印', value: 0 },
      { label: '1 - 穿插列印', value: 1 },
      { label: '2 - DO優先順序列印', value: 2 },
    ];

    this.isDeleteOptions = [
      { label: 'Y', value: 0 },
      { label: 'N', value: 1 },
    ];

    this.editPermission = this.userProfile.userType === UserType.SuperAdmin.toString() || this.permissionCodes.includes(this.editPermissionCode);
    if (this.editPermission) {
      this.permissionCode = this.editPermissionCode;
    }

    this.autoPrintSettingService.getAutoPrintSettingDocTypeOption().subscribe({
      next: (options: any[]) => {
        this.docTypeOptions = options.map(x => ({ label: x, value: x }));
      }
    });
  }



  resetBtnClick() {
    this.searchFormGroup.reset();
    this.searchFormGroup.controls['facility'].setValue(this.laasSetting.facility);
    this.searchFormGroup.controls['deliveryTypes'].setValue(null);
    this.searchFormGroup.controls['docTypes'].setValue(null);
  }

  searchBtnClick(): void {
    this.headerTable.loading = true;
    this.headerTable.cancelRowEdit();
    this.headerTable.isInEditMode = false;
    this.headerTable.setCurrentPage(0);
    this.isOpenedSettingHeaderTab = true;
    this.getHeaderTableData();
  }

  getHeaderTableData() {
    let request: any = this.createGetHeaderRequest();
    request.pageIndex = this.pageInfo.pageIndex,
    request.pageSize = this.pageInfo.pageSize,
    request.sortOrder = this.sortOrder

    this.isHeaderTableLoading = true;

    this.autoPrintSettingService.getAutoPrintSettingHeader(request)
      .pipe(finalize(() => (this.isHeaderTableLoading = false)))
      .subscribe({
        next: (response: any) => {
          if (isNullOrEmpty(response)) {
            throwError(response);
          }
          this.isOpenedSettingSearchFormTab = false;
          this.settingHeaderData = [];

          //Update table
          if (!isNullOrEmpty(response.content)) {
            this.settingHeaderData = deepCopy(response.content) ?? [];
          }
          this.logger.info('settingHeaderData=', JSON.stringify(this.settingHeaderData));

          this.headerTable.updateTableData(this.settingHeaderData);
          if (this.headerTable.selectedRow) {
            this.onRowSelect(this.headerTable.selectedRow);
          }

          // Change page Info
          this.pageInfo.totalRecords = response.totalElements;
          this.headerTable.setPageInfo(this.pageInfo.totalRecords);
        },
        error: (response) => {
          this.settingHeaderData = [];
          this.headerTable.updateTableData(this.settingHeaderData);
          this.pageInfo.totalRecords = 0;
          this.pageInfo.totalPages = 0;
          this.headerTable.setPageInfo(this.pageInfo.totalRecords);
          this.toastService.error(this.translateService.instant('Common.Toast.GetDataFail') + response.status);

        },
        complete: () => {
          this.logger.info('complete...');
          this.headerTable.loading = false;
        },
      });
  }

  createGetHeaderRequest(): AutoPrintSettingGetHeaderRequest {
    let headerRequest: AutoPrintSettingGetHeaderRequest = new AutoPrintSettingGetHeaderRequest();
    headerRequest.facility = this.searchFormGroup.value.facility;
    headerRequest.deliveryTypes = nullIfEmptyArray(this.searchFormGroup.value.deliveryTypes);
    headerRequest.docTypes = nullIfEmptyArray(this.searchFormGroup.value.docTypes);
    return headerRequest;
  }

  onInputHandle(event:any, rowData:any) {
  }

  deleteBtnClick(seqNo: number) {
    this.confirmationService.confirm({
      message: this.translateService.instant('將移除資料庫資料，請問要刪除嗎?'),
      header: this.translateService.instant('系統資訊'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.loader.show();
        this.autoPrintSettingService.deleteAutoPrintSettingHeader(seqNo).subscribe({
          next: result => {
            this.toastService.success('資料刪除完成!!');
            this.searchBtnClick();
          },
          error: async response => {
            this.loader.hide();
            this.headerTable.loading = false;
            var instantMsg = await this.translateService.get("Common.Toast.SaveFail").toPromise();
            var msg = `${laaSFormatString(instantMsg, [response.status, response.error.errorMsg])}\n`;
            this.toastService.error(msg);
          }, complete: () => {
            this.logger.info('complete...');
            this.loader.hide();
            this.headerTable.loading = false;
          },
        });
      },
      reject: (type: any) => {
        switch (type) {
          case ConfirmEventType.REJECT:
            break;
          case ConfirmEventType.CANCEL:
            break;
        }
      }
    });
  }

  createNewRow(rowId: number): any {
    let newRow = {
      seqNo: null,
      facility: this.laasSetting.facility,
      deliveryType: null,
      docType: null,
      numberOfCopies: null,
      isLoopGroup: null,
      printOrder: null,
      createUser: null,
      createDt: null,
      updateUser: null,
      updateDt: null,
      isDelete: 0,
      consigneeKey: null,
      rowId: rowId,
    };
    return newRow;
  }

  dataInputValid(rowData: AutoPrintSettingTableHeaderData): boolean {
    if (rowData) {
      if (isNullOrEmpty(rowData.deliveryType) || isNullOrEmpty(rowData.docType) ||
            isNullOrEmpty(rowData.numberOfCopies) || isNullOrEmpty(rowData.printOrder) || isNullOrEmpty(rowData.isLoopGroup) || isNullOrEmpty(rowData.isDelete)) {
        this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
        return false;
      }
    }
    return true;
  }

  createRow(rowData: any): void {
    this.onRowSelect(rowData);
  }

  onRowSelect(event: any): void {
  }

  saveData(savedDataBean: DataTableSavedBean): void {
    const saveDataRequest = {
      insertList: savedDataBean.newList,
      updateList: savedDataBean.updatedList,
      updateUser: this.userProfile.userName
    };
    this.autoPrintSettingService.saveAutoPrintSettingHeader(saveDataRequest).subscribe({
      next: () => {
        this.headerTable.saveSuccessEvent();
        this.searchBtnClick();
        this.toastService.success(this.translateService.instant('Common.Toast.SaveSuccess'));
      },
      error: async response => {
        this.headerTable.loading = false;
        var instantMsg = await this.translateService.get("Common.Toast.SaveFail").toPromise();
        var msg = `${laaSFormatString(instantMsg, [response.status, response.error.errorMsg])}\n`;
        this.toastService.error(msg);
      }, complete: () => {
        this.logger.info('complete...');
        this.headerTable.loading = false;
      },
    });
  }

  restoreData(): void {
    this.ngOnInit();
  }

  tablePageChange(event: any): void {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
    this.getHeaderTableData();
  }

  tableSort(event: any): void {
    this.sortOrder = { property: event.field, direction: event.order };
    this.headerTable.setCurrentPage(0);
    this.getHeaderTableData();
  }

}
